/**
 * services/oee/summary.service.ts - Výpočet souhrnného OEE
 *
 * FUNKCE: Vypočítá průměrné OEE komponenty (Availability, Performance, Quality, OEE)
 * za zadané období.
 *
 * PROCES VÝPOČTU:
 * 1. Načteme směny + plánované/neplánované odstávky z DB (jeden dotaz)
 * 2. Sestavíme cycle time resolver (pro výpočet Performance)
 * 3. Předpočítáme sjednocené odstávky (sweep-line)
 * 4. Pro každou směnu:
 *    a) Spočítáme průnik směny s rozsahem
 *    b) Spočítáme X (dostupný čas = čas směny - plánované odstávky)
 *    c) Spočítáme availability minutes (X - neplánované odstávky)
 *    d) Spočítáme ideal runtime (suma: count_i * cycleTime_i)
 *    e) Vypočítáme OEE komponenty pomocí computeOeeComponents()
 * 5. Zprůměrujeme komponenty přes všechny směny
 *
 * DŮLEŽITÉ POZNÁMKY:
 * - Performance je počítáno přes ideální runtime (nový vzorec), ne přes planCount
 * - Availability = 1 - (neplánované prostoje / X)
 * - Quality = OK / (OK + NOK)
 * - OEE = Availability * Performance * Quality
 * - Pokud chybí cycle time pro nějaký produkt, zaznamenáváme to do missingCycleTimes
 *
 * @param filters Obsahuje from, to, locationIds, workplaceIds, projectIds, shiftTypeIds
 * @returns {oee, availability, performance, quality, meta}
 */

import { OeeBatchRepository } from '@/libs/tpm-analytics/data/oee-batch.repository';
import {
    buildUnionIntervalsByWorkplace,
    sumOverlapMinutes,
    computeIntervalIntersectionMinutes,
} from '@/libs/tpm-analytics/core/downtime.utils';
import { computeOeeComponents } from '@/libs/tpm-analytics/core/oee.calculator';
import { buildCycleTimeResolver, buildLocationChainsForShifts } from '@/libs/tpm-analytics/core/cycle-time.resolver';
import { OeeSummary, OeeSummaryFilters } from '@/libs/tpm-analytics/core/types';
import {
    getMissingCycleTimes,
    reportMissingCycleTime,
    resetMissingCycleTimes,
} from '@/libs/tpm-analytics/core/missing-cycle-times';

export async function getOeeSummary(filters: OeeSummaryFilters): Promise<OeeSummary> {
    // POZNÁMKA: resetMissingCycleTimes() se volá v komponentě na začátku load(), ne zde
    // Důvod: Pokud se volá více služeb současně (summary + daily + workplace), reset by mazal data
    const filtersArg = {
        locationIds: filters.locationIds || (filters.locationId ? [filters.locationId] : []),
        workplaceIds: filters.workplaceIds || (filters.workplaceId ? [filters.workplaceId] : []),
        projectIds: filters.projectIds,
        shiftTypeIds: filters.shiftTypeIds,
        shiftTeamTypeIds: filters.shiftTeamTypeIds,
    };
    // FÁZE 1: Načtení dat z DB (jeden batch dotaz)
    const { shifts, unplanned, planned } = await OeeBatchRepository.fetchCurrent(filters.from, filters.to, filtersArg);

    // FÁZE 2: Příprava resolverů a pomocných struktur
    // Sestavíme cycle time resolver (pro Performance výpočet)
    const resolveCt = await buildCycleTimeResolver(shifts);
    // Sestavíme location chains (pro CT resolution po předcích)
    const locChainsByWp = buildLocationChainsForShifts(shifts);
    const rangeFromMs = Date.parse(filters.from);
    const rangeToMs = Date.parse(filters.to);

    // FÁZE 3: Předpočítáme sjednocené odstávky (sweep-line algoritmus)
    // Výhoda: Místo O(n*m) průniků pro každou směnu a odstávku máme O(n+m) + O(k) per směna
    const plannedUnion = buildUnionIntervalsByWorkplace(planned as any[], filters.from, filters.to);
    const unplannedUnion = buildUnionIntervalsByWorkplace(unplanned as any[], filters.from, filters.to);

    // FÁZE 4: Sčítání surových dat ze všech směn
    // DŮLEŽITÉ: OEE procenta NESMÍME průměrovat! Musíme sečíst surová data (minuty, counts)
    // a teprve PAK z nich spočítat procenta.
    let totalOk = 0;
    let totalNok = 0;
    let totalPlanByCt = 0; // legacy, už nepoužíváme
    let totalXMinutes = 0; // dostupný čas (shift - plánované odstávky)
    let totalUnplannedMinutes = 0; // neplánované odstávky
    let totalIdealRuntimeMinutes = 0; // ideální runtime (suma count * cycleTime)
    let shiftCount = 0;

    for (const s of shifts) {
        if (!s?.fromDate) continue;
        const wid: string | null = (s as any).workplaceId ?? s.workplaceId ?? null;
        const sFromMs = Date.parse(s.fromDate as string);
        const sToMs = Date.parse((s.toDate as string) ?? (s.fromDate as string));
        if (!isFinite(sFromMs) || !isFinite(sToMs) || !isFinite(rangeFromMs) || !isFinite(rangeToMs)) continue;

        // Průnik směny s rozsahem [filters.from, filters.to]
        const fromIso = new Date(Math.max(rangeFromMs, sFromMs)).toISOString();
        const toIso = new Date(Math.min(rangeToMs, sToMs)).toISOString();
        const shiftMinutes = (() => {
            const a = Date.parse(fromIso),
                b = Date.parse(toIso);
            return b > a ? Math.floor((b - a) / 60000) : 0;
        })();

        // Minuty překryvu s plánovanými/neplánovanými odstávkami
        const plannedMin = sumOverlapMinutes(plannedUnion.get(wid), Date.parse(fromIso), Date.parse(toIso));
        let unplannedMin = sumOverlapMinutes(unplannedUnion.get(wid), Date.parse(fromIso), Date.parse(toIso));

        // DŮLEŽITÉ: Odečteme překryv plánovaných a neplánovaných odstávek
        // Pokud je stroj v plánované odstávce, nemůže být zároveň v neplánované.
        // Plánovaná má přednost → odečteme překryv z neplánované
        const overlapMin = computeIntervalIntersectionMinutes(
            plannedUnion.get(wid),
            unplannedUnion.get(wid),
            Date.parse(fromIso),
            Date.parse(toIso)
        );
        unplannedMin = Math.max(0, unplannedMin - overlapMin);

        // X = dostupný čas (čas směny minus plánované odstávky)
        const X = Math.max(0, shiftMinutes - plannedMin);

        // Výpočet ideálního runtime přes všechny produkty ve směně
        // Ideal Runtime = suma(count_i * cycleTime_i) pro všechny produkty
        const prods: any[] = Array.isArray((s as any)?.shiftProductions) ? ((s as any).shiftProductions as any[]) : [];
        const locChain = locChainsByWp.get(wid) || [];
        let idealRuntimeMinutes = 0;

        for (const p of prods) {
            const pid: string | null = (p as any)?.productId ?? (p as any)?.product?.id ?? null;
            const pcode: string | null = (p as any)?.product?.code ?? null;
            const pname: string | null = (p as any)?.product?.name ?? null;
            const pcid: string | null = (p as any)?.product?.productCollectionId ?? null;
            const pcname: string | null = (p as any)?.product?.productCollectionName ?? null;
            const count = (Number((p as any)?.okCount ?? 0) || 0) + (Number((p as any)?.nokCount ?? 0) || 0);
            if (!pid || count <= 0) continue;

            // Najdeme cycle time pro tento produkt + pracoviště (s fallbackem na ID/CODE/NAME)
            const wpCode: string | null = (s as any)?.workplace?.code ?? null;
            const wpName: string | null = (s as any)?.workplace?.name ?? null;
            const res = resolveCt({ 
                workplaceId: wid, 
                workplaceCode: wpCode,
                workplaceName: wpName,
                productId: pid, 
                productCode: pcode,
                productName: pname,
                productCollectionId: pcid,
                productCollectionName: pcname,
                locationChain: locChain 
            });
            const ctMin = res.minutes;

            if (typeof ctMin === 'number' && ctMin > 0) {
                // Máme cycle time → přičteme k ideálnímu runtime
                idealRuntimeMinutes += count * ctMin;
            } else {
                // Chybí cycle time → zaznamenáme pro reporting
                if (res.matchType == null) {
                    const code: string | null = (p as any)?.product?.code ?? null;
                    const wpCode: string | null = (s as any)?.workplace?.code ?? null;
                    reportMissingCycleTime({
                        productId: pid,
                        productCode: code,
                        count,
                        workplaceCodes: wpCode ? [wpCode] : undefined,
                    });
                }
            }
        }

        // Omezíme idealRuntimeMinutes na max dostupný čas pro výrobu (X - neplánované odstávky)
        // aby nedošlo k přetečení do další směny při špatně nastavené normě
        const maxAvailableMinutes = Math.max(0, X - unplannedMin);
        idealRuntimeMinutes = Math.min(idealRuntimeMinutes, maxAvailableMinutes);

        const ok = Number(s.okCount ?? 0) || 0;
        const nok = Number(s.nokCount ?? 0) || 0;

        // AKUMULUJEME surová data (ne procenta!)
        totalOk += ok;
        totalNok += nok;
        totalPlanByCt += 0; // legacy
        totalXMinutes += X;
        totalUnplannedMinutes += unplannedMin;
        totalIdealRuntimeMinutes += idealRuntimeMinutes;
        shiftCount += 1;
    }

    // FÁZE 5: Teprve TEĎ spočítáme OEE procenta z agregovaných dat
    // Takhle získáme matematicky správné výsledky
    const finalComponents = computeOeeComponents(
        totalOk,
        totalNok,
        totalPlanByCt,
        totalXMinutes,
        totalUnplannedMinutes,
        totalIdealRuntimeMinutes
    );

    return {
        oee: finalComponents.oee,
        availability: finalComponents.availability,
        performance: finalComponents.performance,
        quality: finalComponents.quality,
        meta: { shiftCount, totalOk, totalNok, totalPlanByCt, totalXMinutes, totalUnplannedMinutes },
    } as OeeSummary;
}
